import { AppBar, Toolbar, Typography, Button, Box, Menu, MenuItem, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ExpandMore,
  ExpandLess,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  FitnessCenter as FitnessCenterIcon,
  VideoLibrary as VideoLibraryIcon,
  EventNote as EventNoteIcon,
  Groups as GroupsIcon,
  ViewModule as ViewModuleIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Quiz as QuizIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Style as StyleIcon,
  SportsVolleyball as SportsVolleyballIcon,
} from "@mui/icons-material"; // Import des icônes
import theme from "../styles/theme";
import Logo from "./Logo";
import { isQuizEnabled, isCardsEnabled, isSchemasEnabled } from "../api";

interface HeaderProps {
  user: any;
  onLogout: () => void;
}

export default function Header({ user, onLogout }: HeaderProps) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [superAdminAnchorEl, setSuperAdminAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // État pour le menu mobile
  const isCoach = user?.role === "coach";
  const hasSuperAdminFlag = user?.is_superadmin === true || user?.is_superadmin === 1 || user?.is_superadmin === "1";
  const isSuperAdmin = hasSuperAdminFlag || ["staff", "superadmin"].includes(user?.role);
  const [quizEnabled, setQuizEnabled] = useState(true);
  const [cardsEnabled, setCardsEnabled] = useState(true);
  const [schemasEnabled, setSchemasEnabled] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    isQuizEnabled()
      .then((enabled) => { if (!cancelled) setQuizEnabled(enabled); })
      .catch(() => { if (!cancelled) setQuizEnabled(true); });
    isCardsEnabled()
      .then((enabled) => { if (!cancelled) setCardsEnabled(enabled); })
      .catch(() => { if (!cancelled) setCardsEnabled(true); });
    isSchemasEnabled()
      .then((enabled) => { if (!cancelled) setSchemasEnabled(enabled); })
      .catch(() => { if (!cancelled) setSchemasEnabled(true); });
    return () => { cancelled = true; };
  }, [user]);

  const handleLogout = () => {
    onLogout();
    navigate("/login");
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSuperAdminMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSuperAdminAnchorEl(event.currentTarget);
  };

  const handleSuperAdminMenuClose = () => {
    setSuperAdminAnchorEl(null);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  return (
    <AppBar position="static" color="primary" style={{ marginBottom: 20 }}>
      <Toolbar>
        <Box sx={{ flexGrow: 1 }}>
          <Logo size="medium" />
        </Box>
        {user && (
          <>
            {/* Menu pour les écrans larges */}
            <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 2 }}>
              <Typography variant="body1">Bonjour, {user.username}</Typography>
              {quizEnabled && (
                <Button
                  color="inherit"
                  startIcon={<QuizIcon />}
                  onClick={() => navigate("/schema-quiz")}
                >
                  Quiz Placement
                </Button>
              )}
              {isCoach && (
                <>
                  <Button
                    color="inherit"
                    startIcon={<DashboardIcon />}
                    onClick={() => navigate("/dashboard")}
                  >
                    Tableau de bord
                  </Button>
                  <Button
                    color="inherit"
                    onClick={handleMenuOpen}
                    sx={{ textTransform: "none", display: "flex", alignItems: "center" }}
                  >
                    Gérer
                    {anchorEl ? <ExpandLess sx={{ marginLeft: 1 }} /> : <ExpandMore sx={{ marginLeft: 1 }} />}
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={() => { handleMenuClose(); navigate("/manage-players"); }}>
                      <PeopleIcon sx={{ mr: 1 }} /> Gérer les joueurs
                    </MenuItem>
                    <MenuItem onClick={() => { handleMenuClose(); navigate("/manage-exercises"); }}>
                      <FitnessCenterIcon sx={{ mr: 1 }} /> Gérer les exercices
                    </MenuItem>
                    <MenuItem onClick={() => { handleMenuClose(); navigate("/manage-videos"); }}>
                      <VideoLibraryIcon sx={{ mr: 1 }} /> Gérer les vidéos
                    </MenuItem>
                    <MenuItem onClick={() => { handleMenuClose(); navigate("/sessions"); }}>
                      <EventNoteIcon sx={{ mr: 1 }} /> Gérer les sessions d'éval.
                    </MenuItem>
                    <MenuItem onClick={() => { handleMenuClose(); navigate("/manage-teams"); }}>
                      <GroupsIcon sx={{ mr: 1 }} /> Gérer les équipes
                    </MenuItem>
                    <MenuItem onClick={() => { handleMenuClose(); navigate("/compo-create"); }}>
                      <ViewModuleIcon sx={{ mr: 1 }} /> Créateur de compos
                    </MenuItem>
                    <MenuItem onClick={() => { handleMenuClose(); navigate("/schema-studio"); }}>
                      <ViewModuleIcon sx={{ mr: 1 }} /> Schémas (Studio)
                    </MenuItem>
                    <MenuItem onClick={() => { handleMenuClose(); navigate("/admin-rotations"); }}>
                      <ViewModuleIcon sx={{ mr: 1 }} /> Rotations 5-1 (Étape 1)
                    </MenuItem>
                    <MenuItem onClick={() => { handleMenuClose(); navigate("/suivi-match"); }}>
                      <SportsVolleyballIcon sx={{ mr: 1 }} /> Suivi de match 5-1
                    </MenuItem>
                    {cardsEnabled && (
                      <MenuItem onClick={() => { handleMenuClose(); navigate("/cartons"); }}>
                        <StyleIcon sx={{ mr: 1 }} /> Cartons
                      </MenuItem>
                    )}
                  </Menu>
                  <Button
                    color="inherit"
                    startIcon={<AssessmentIcon />}
                    onClick={() => navigate("/evaluation")}
                    style={{
                      fontWeight: "bold",
                      backgroundColor: theme.palette.secondary.light,
                      borderRadius: 4,
                    }}
                  >
                    Faire une Évaluation
                  </Button>
                  <Button
                    color="inherit"
                    startIcon={<TrendingUpIcon />}
                    onClick={() => navigate("/evolution")}
                    style={{
                      fontWeight: "bold",
                      backgroundColor: theme.palette.info.light,
                      borderRadius: 4,
                    }}
                  >
                    Évolution joueurs
                  </Button>
                </>
              )}
              {isSuperAdmin && (
                <>
                  <Button
                    color="inherit"
                    startIcon={<AdminPanelSettingsIcon />}
                    onClick={handleSuperAdminMenuOpen}
                    style={{
                      fontWeight: "bold",
                      backgroundColor: theme.palette.warning.light,
                      borderRadius: 4,
                      textTransform: "none",
                    }}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    Super Admin
                    {superAdminAnchorEl ? <ExpandLess sx={{ marginLeft: 1 }} /> : <ExpandMore sx={{ marginLeft: 1 }} />}
                  </Button>
                  <Menu
                    anchorEl={superAdminAnchorEl}
                    open={Boolean(superAdminAnchorEl)}
                    onClose={handleSuperAdminMenuClose}
                  >
                    <MenuItem onClick={() => { handleSuperAdminMenuClose(); navigate("/superadmin-passwords"); }}>
                      <AssessmentIcon sx={{ mr: 1 }} /> Passwords & Tracking
                    </MenuItem>
                    <MenuItem onClick={() => { handleSuperAdminMenuClose(); navigate("/superadmin-quiz-settings"); }}>
                      <SettingsIcon sx={{ mr: 1 }} /> Réglages Quiz
                    </MenuItem>
                  </Menu>
                </>
              )}
              {user.role === "player" && (
                <>
                  <Button
                    color="inherit"
                    startIcon={<PersonIcon />}
                    onClick={() => navigate(`/player/${user.id}`)}
                  >
                    Mon profil
                  </Button>
                  {schemasEnabled && (
                    <Button
                      color="inherit"
                      startIcon={<ViewModuleIcon />}
                      onClick={() => navigate("/schema-studio")}
                    >
                      Schémas
                    </Button>
                  )}
                </>
              )}
              <Button color="inherit" startIcon={<LogoutIcon />} onClick={handleLogout}>
                Déconnexion
              </Button>
            </Box>

            {/* Menu pour les écrans mobiles */}
            <Box sx={{ display: { xs: "flex", md: "none" } }}>
              <IconButton color="inherit" onClick={toggleMobileMenu}>
                <MenuIcon />
              </IconButton>
              {mobileMenuOpen && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "64px",
                    left: 0,
                    width: "100%",
                    backgroundColor: theme.palette.background.default, // Fond clair
                    color: theme.palette.text.primary, // Texte sombre
                    boxShadow: theme.shadows[4],
                    zIndex: 10,
                  }}
                >
                  <MenuItem
                    sx={{ color: theme.palette.text.primary }} // Texte sombre
                    onClick={() => { toggleMobileMenu(); navigate("/dashboard"); }}
                  >
                    <DashboardIcon sx={{ mr: 1 }} /> Tableau de bord
                  </MenuItem>
                  {quizEnabled && (
                    <MenuItem
                      sx={{ color: theme.palette.text.primary }}
                      onClick={() => { toggleMobileMenu(); navigate("/schema-quiz"); }}
                    >
                      <QuizIcon sx={{ mr: 1 }} /> Quiz Placement
                    </MenuItem>
                  )}
                  {isCoach && (
                    <>
                      <MenuItem
                        sx={{ color: theme.palette.text.primary }}
                        onClick={() => { toggleMobileMenu(); navigate("/manage-players"); }}
                      >
                        <PeopleIcon sx={{ mr: 1 }} /> Gérer les joueurs
                      </MenuItem>
                      <MenuItem
                        sx={{ color: theme.palette.text.primary }}
                        onClick={() => { toggleMobileMenu(); navigate("/manage-exercises"); }}
                      >
                        <FitnessCenterIcon sx={{ mr: 1 }} /> Gérer les exercices
                      </MenuItem>
                      <MenuItem
                        sx={{ color: theme.palette.text.primary }}
                        onClick={() => { toggleMobileMenu(); navigate("/manage-videos"); }}
                      >
                        <VideoLibraryIcon sx={{ mr: 1 }} /> Gérer les vidéos
                      </MenuItem>
                      <MenuItem
                        sx={{ color: theme.palette.text.primary }}
                        onClick={() => { toggleMobileMenu(); navigate("/sessions"); }}
                      >
                        <EventNoteIcon sx={{ mr: 1 }} /> Gérer les sessions
                      </MenuItem>
                      <MenuItem
                        sx={{ color: theme.palette.text.primary }}
                        onClick={() => { toggleMobileMenu(); navigate("/manage-teams"); }}
                      >
                        <GroupsIcon sx={{ mr: 1 }} /> Gérer les équipes
                      </MenuItem>
                      <MenuItem
                        sx={{ color: theme.palette.text.primary }}
                        onClick={() => { toggleMobileMenu(); navigate("/compo-create"); }}
                      >
                        <ViewModuleIcon sx={{ mr: 1 }} /> Createur de compo
                      </MenuItem>
                      <MenuItem
                        sx={{ color: theme.palette.text.primary }}
                        onClick={() => { toggleMobileMenu(); navigate("/schema-studio"); }}
                      >
                        <ViewModuleIcon sx={{ mr: 1 }} /> Schémas (Studio)
                      </MenuItem>
                      <MenuItem
                        sx={{ color: theme.palette.text.primary }}
                        onClick={() => { toggleMobileMenu(); navigate("/admin-rotations"); }}
                      >
                        <ViewModuleIcon sx={{ mr: 1 }} /> Rotations 5-1 (Étape 1)
                      </MenuItem>
                      <MenuItem
                        sx={{ color: theme.palette.text.primary }}
                        onClick={() => { toggleMobileMenu(); navigate("/suivi-match"); }}
                      >
                        <SportsVolleyballIcon sx={{ mr: 1 }} /> Suivi de match 5-1
                      </MenuItem>
                      {cardsEnabled && (
                        <MenuItem
                          sx={{ color: theme.palette.text.primary }}
                          onClick={() => { toggleMobileMenu(); navigate("/cartons"); }}
                        >
                          <StyleIcon sx={{ mr: 1 }} /> Cartons
                        </MenuItem>
                      )}
                      <MenuItem
                        sx={{ color: theme.palette.text.primary }}
                        onClick={() => { toggleMobileMenu(); navigate("/evaluation"); }}
                      >
                        <AssessmentIcon sx={{ mr: 1 }} /> Évaluations
                      </MenuItem>
                      <MenuItem
                        sx={{ color: theme.palette.text.primary }}
                        onClick={() => { toggleMobileMenu(); navigate("/evolution"); }}
                      >
                        <TrendingUpIcon sx={{ mr: 1 }} /> Évolution joueurs
                      </MenuItem>
                    </>
                  )}
                  {isSuperAdmin && (
                    <>
                      <Typography
                        sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: theme.palette.text.secondary }}
                      >
                        Super Admin
                      </Typography>
                      <MenuItem
                        sx={{ color: theme.palette.text.primary }}
                        onClick={() => { toggleMobileMenu(); navigate("/superadmin-passwords"); }}
                      >
                        <AssessmentIcon sx={{ mr: 1 }} /> Passwords & Tracking
                      </MenuItem>
                      <MenuItem
                        sx={{ color: theme.palette.text.primary }}
                        onClick={() => { toggleMobileMenu(); navigate("/superadmin-quiz-settings"); }}
                      >
                        <SettingsIcon sx={{ mr: 1 }} /> Réglages Quiz
                      </MenuItem>
                    </>
                  )}
                  {user.role === "player" && (
                    <>
                      <MenuItem
                        sx={{ color: theme.palette.text.primary }}
                        onClick={() => { toggleMobileMenu(); navigate(`/player/${user.id}`); }}
                      >
                        <PersonIcon sx={{ mr: 1 }} /> Mon profil
                      </MenuItem>
                      {schemasEnabled && (
                        <MenuItem
                          sx={{ color: theme.palette.text.primary }}
                          onClick={() => { toggleMobileMenu(); navigate("/schema-studio"); }}
                        >
                          <ViewModuleIcon sx={{ mr: 1 }} /> Schémas
                        </MenuItem>
                      )}
                    </>
                  )}
                  <MenuItem
                    sx={{ color: theme.palette.text.primary }}
                    onClick={() => { toggleMobileMenu(); handleLogout(); }}
                  >
                    <LogoutIcon sx={{ mr: 1 }} /> Déconnexion
                  </MenuItem>
                </Box>
              )}
            </Box>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}
